import type { BrowserHostedSessionClient } from '@owlbert/core'
import { Alert, Select, Text, TextInput } from '@mantine/core'
import React from 'react'
import {
  cloudLlmModelsToSelectData,
  fetchOwlbertCloudLlmModelOptions,
  resolveAssistantLlmSelectValue,
  type CloudLlmModelOption,
  type LlmCatalogClient,
} from '../cloudLlmModels'
import { selectComboboxAboveModal } from '../theme'

export type AssistantModelFieldProps = {
  value: string
  onChange: (next: string) => void
  /** Signed in with a hosted session client (loads `llm.getModels` for the account). */
  catalogReady: boolean
  bundle: BrowserHostedSessionClient | null
  disabled?: boolean
}

/**
 * When signed in with a hosted session client, shows models assigned to the user (`llm.getModels`),
 * excluding custom endpoints. Otherwise falls back to a plain text field (legacy / BFF-only).
 */
export const AssistantModelField: React.FC<AssistantModelFieldProps> = ({
  value,
  onChange,
  catalogReady,
  bundle,
  disabled = false,
}) => {
  const [models, setModels] = React.useState<CloudLlmModelOption[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!catalogReady || !bundle?.client) {
      setModels([])
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    void fetchOwlbertCloudLlmModelOptions(bundle.client as LlmCatalogClient)
      .then((rows) => {
        if (!cancelled) setModels(rows)
      })
      .catch((e) => {
        if (!cancelled) setError(String((e as Error)?.message ?? e))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [catalogReady, bundle?.client])

  React.useEffect(() => {
    if (!catalogReady || !bundle?.client || models.length === 0) return
    const resolved = resolveAssistantLlmSelectValue(value, models)
    if (resolved !== value) onChange(resolved)
  }, [catalogReady, bundle?.client, models, value, onChange])

  if (!catalogReady || !bundle?.client) {
    return (
      <TextInput
        label="Model (LLM id)"
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        description="Sign in with Owlbert cloud to pick from models assigned to your account. Otherwise enter a model id your server accepts."
        disabled={disabled}
      />
    )
  }

  if (error) {
    return (
      <>
        <Alert color="red" title="Could not load models">
          {error}
        </Alert>
        <TextInput label="Model (LLM id)" value={value} onChange={(e) => onChange(e.currentTarget.value)} disabled={disabled} />
      </>
    )
  }

  const data = cloudLlmModelsToSelectData(models)

  return (
    <>
      <Select
        label="Model"
        description="Models assigned to your Owlbert cloud account."
        placeholder={loading ? 'Loading models…' : models.length === 0 ? 'No models available' : 'Choose a model'}
        data={data}
        value={value && data.some((d) => d.value === value) ? value : null}
        onChange={(v) => {
          if (v) onChange(v)
        }}
        searchable
        nothingFoundMessage="No matching model"
        disabled={disabled || (loading && models.length === 0)}
        comboboxProps={{ withinPortal: false }}
      />
      {!loading && models.length === 0 ? (
        <Text size="xs" c="dimmed">
          No chat models are available for your account. Check Settings → LLMs or contact your administrator.
        </Text>
      ) : null}
    </>
  )
}
